/**
 * InformantFormInput
 * ==================
 *
 * A labelled text input that matches the form input styling used throughout
 * the informant management modals.  Handles validation error display,
 * input sanitization, and accessible labels in a single reusable component.
 *
 * This avoids duplicating the Tailwind class strings across every input in
 * the SPLIT / ADD / Replace / Email-change flows.
 */

import React, { useId } from 'react';

/**
 * @param {Object}   props
 * @param {string}   props.label          – Visible label text
 * @param {string}   props.value          – Controlled input value
 * @param {Function} props.onChange        – Called with the raw event; sanitize in the parent
 * @param {string}   [props.type]         – Input type (defaults to 'text')
 * @param {string}   [props.placeholder]  – Placeholder text
 * @param {string}   [props.error]        – Validation error message to display
 * @param {boolean}  [props.disabled]     – Whether the input is disabled
 * @param {boolean}  [props.required]     – Whether the field is required
 * @param {string}   [props.autoComplete] – HTML autocomplete hint
 */
export default function InformantFormInput({
  label,
  value,
  onChange,
  onBlur,
  type = 'text',
  placeholder = '',
  error = '',
  disabled = false,
  required = false,
  autoComplete,
}) {
  const id = useId();

  return (
    <div>
      <label
        htmlFor={id}
        className="block text-xs font-medium text-gray-700 mb-1"
      >
        {label}
        {required && <span className="text-red-600 ml-0.5">*</span>}
      </label>

      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        autoComplete={autoComplete}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : undefined}
        className={`
          w-full px-3 py-2 text-sm border rounded-md
          focus:outline-none focus:ring-2
          ${error
            ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
            : 'border-gray-300 focus:ring-violet-500 focus:border-violet-500'
          }
          ${disabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}
        `}
      />

      {error && (
        <p
          id={`${id}-error`}
          className="mt-1 text-xs font-semibold text-red-700 bg-red-50 px-2 py-1 rounded-md"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  );
}
